import type { MouseEvent } from "react";
import clsx from "clsx";
import type { MissionSummary } from "@/services/api/agentic-v2";
import { VirtualList } from "@/components/mission-control/VirtualList";
import { MissionRow } from "./MissionRow";
import type { useAgentsList } from "./useAgentsList";

/** Initial row height before DOM measurement; real heights are measured at runtime. */
export const MISSION_ROW_ESTIMATE_PX = 88;
export const MISSION_ROW_GAP_PX = 8;

export interface MissionsVirtualListProps {
  missions: MissionSummary[];
  agents: ReturnType<typeof useAgentsList>;
  refreshing: boolean;
  onMissionClick: (mission: MissionSummary) => void;
  onMissionContextMenu?: (event: MouseEvent, mission: MissionSummary) => void;
  scrollElement: HTMLDivElement | null;
  scrollReady: boolean;
  listKey?: string;
  /** Mission ``_id`` to scroll into view before list-focus flash. */
  focusMissionId?: string | null;
  className?: string;
}

/** Virtualised mission rows — spacing and measurement handled by the shared ``VirtualList``. */
export function MissionsVirtualList({
  missions,
  agents,
  refreshing,
  onMissionClick,
  onMissionContextMenu,
  scrollElement,
  scrollReady,
  listKey,
  focusMissionId = null,
  className,
}: MissionsVirtualListProps) {
  return (
    <VirtualList
      className={clsx(
        "transition-opacity duration-200",
        refreshing && "pointer-events-none opacity-60",
        className,
      )}
      items={missions}
      getKey={(mission) => mission._id}
      estimateSize={() => MISSION_ROW_ESTIMATE_PX}
      gap={MISSION_ROW_GAP_PX}
      scrollElement={scrollElement}
      scrollReady={scrollReady}
      listKey={listKey}
      focusItemKey={focusMissionId}
      renderItem={(mission) => (
        <div onContextMenu={onMissionContextMenu && ((event) => onMissionContextMenu(event, mission))}>
          <MissionRow mission={mission} onClick={onMissionClick} agents={agents} />
        </div>
      )}
    />
  );
}
